import { GlButton, GlTooltipDirective, GlLoadingIcon } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { computed, defineAsyncComponent } from '../../../lib/utils/vue3compat/vue.js?vue3';
import { __ } from '~/locale';
import { logError } from '~/lib/logger';
import { copyToClipboard } from '~/lib/utils/copy_to_clipboard';
import { visitUrl } from '~/lib/utils/url_utility';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { createAlert } from '../../../alert.js?vue3';
import getRefMixin from '~/repository/mixins/get_ref';
import initSourcegraph from '~/sourcegraph';
import Shortcuts from '../../../behaviors/shortcuts/shortcuts.js?vue3';
import { keyboardShortcutsDisabled } from '~/behaviors/shortcuts/shortcuts_disabled';
import { keysFor, START_SEARCH_PROJECT_FILE } from '~/behaviors/shortcuts/keybindings';
import { sanitize } from '~/lib/dompurify';
import { InternalEvents } from '../../../tracking/index.js?vue3';
import { FIND_FILE_BUTTON_CLICK } from '~/tracking/constants';
import { showSingleFileEditorForkSuggestion, showWebIdeForkSuggestion, isIdeTarget, forkSuggestionForSelectedEditor } from '~/repository/utils/fork_suggestion_utils';
import { isUsingLfs } from '~/repository/utils/storage_info_utils';
import blobControlsQuery from '~/repository/queries/blob_controls.query.graphql';
import userGitpodInfo from '~/repository/queries/user_gitpod_info.query.graphql';
import applicationInfoQuery from '~/repository/queries/application_info.query.graphql';
import { getRefType } from '~/repository/utils/ref_type';
import OpenMrBadge from '../../../badges/components/open_mr_badge/open_mr_badge.vue?vue3';
import BlobOverflowMenu from '../../../../../../ee/app/assets/javascripts/repository/components/header_area/blob_overflow_menu.vue?vue3';
import ForkSuggestionModal from './fork_suggestion_modal.vue?vue3';
import { TEXT_FILE_TYPE, EMPTY_FILE, DEFAULT_BLOB_INFO } from '../../constants';
import { NO_MODIFY_PERMISSION_MESSAGE } from './constants';
export default {
    name: 'BlobControls',
    i18n: {
        findFile: __('Find file'),
        errorMessage: __('An error occurred while loading file controls. Refresh the page.'),
        archivedProjectTooltip: __('You cannot edit files in archived projects'),
        noPermissionTooltip: NO_MODIFY_PERMISSION_MESSAGE,
        lfsFileTooltip: __('You cannot edit files stored in LFS')
    },
    buttonClassList: '@sm/panel:gl-w-auto gl-w-full @sm/panel:gl-mt-0 gl-mt-3',
    components: {
        OpenMrBadge,
        GlButton,
        GlLoadingIcon,
        BlobOverflowMenu,
        ForkSuggestionModal,
        WebIdeLink: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/vue_shared/components/web_ide_link.vue?vue3')),
        LockButton: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/repository/components/header_area/blob_controls_lock_button.vue?vue3'))
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        getRefMixin,
        InternalEvents.mixin()
    ],
    inject: [
        'currentRef',
        'showWebIdeButton'
    ],
    provide () {
        return {
            blobInfo: computed(()=>this.blobInfo ?? DEFAULT_BLOB_INFO.repository.blobs.nodes[0]),
            currentRef: computed(()=>this.currentRef ?? this.blobInfo.ref)
        };
    },
    props: {
        projectPath: {
            type: String,
            required: true
        },
        projectIdAsNumber: {
            type: Number,
            required: true
        },
        refType: {
            type: String,
            required: false,
            default: null
        },
        isBinary: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    apollo: {
        project: {
            query: blobControlsQuery,
            variables () {
                return {
                    projectPath: this.projectPath,
                    filePath: this.filePath,
                    ref: this.ref,
                    refType: getRefType(this.refType)
                };
            },
            skip () {
                return !this.filePath;
            },
            error (error) {
                this.hasProjectQueryErrors = true;
                createAlert({
                    message: this.$options.i18n.errorMessage
                });
                logError(`Failed to fetch blob controls. See exception details for more information.`, error);
                Sentry.captureException(error);
            }
        },
        currentUser: {
            query: userGitpodInfo,
            error (error) {
                createAlert({
                    message: this.$options.i18n.errorMessage
                });
                logError(`Failed to fetch current user. See exception details for more information.`, error);
                Sentry.captureException(error);
            }
        },
        gitpodEnabled: {
            query: applicationInfoQuery,
            error (error) {
                createAlert({
                    message: this.$options.i18n.errorMessage
                });
                logError(`Failed to fetch application info. See exception details for more information.`, error);
                Sentry.captureException(error);
            }
        }
    },
    data () {
        return {
            project: {},
            currentUser: {},
            gitpodEnabled: false,
            isForkSuggestionModalVisible: false,
            hasProjectQueryErrors: false
        };
    },
    computed: {
        isLoadingRepositoryBlob () {
            return this.$apollo.queries.project.loading;
        },
        filePath () {
            return this.$route.params.path;
        },
        blobInfo () {
            return this.project?.repository?.blobs?.nodes[0] || {};
        },
        repository () {
            return this.project?.repository || DEFAULT_BLOB_INFO.repository;
        },
        userPermissions () {
            return this.project?.userPermissions || DEFAULT_BLOB_INFO.userPermissions;
        },
        isUsingLfs () {
            return isUsingLfs(this.blobInfo);
        },
        isBinaryFileType () {
            return this.isBinary || this.blobInfo.simpleViewer?.fileType !== TEXT_FILE_TYPE && this.blobInfo.simpleViewer?.fileType !== EMPTY_FILE;
        },
        shortcuts () {
            return {
                findFile: keysFor(START_SEARCH_PROJECT_FILE)[0]
            };
        },
        findFileTooltip () {
            if (keyboardShortcutsDisabled()) return null;
            const { description } = START_SEARCH_PROJECT_FILE;
            const shortcutKey = this.shortcuts.findFile;
            return this.formatTooltipWithShortcut(description, shortcutKey);
        },
        shouldShowSingleFileEditorForkSuggestion () {
            return showSingleFileEditorForkSuggestion(this.userPermissions, this.isUsingLfs, this.blobInfo.canModifyBlob);
        },
        shouldShowWebIdeForkSuggestion () {
            return showWebIdeForkSuggestion(this.userPermissions, this.isUsingLfs, this.blobInfo.canModifyBlobWithWebIde);
        },
        isWebIdeDisabled () {
            return Object.values(this.webIdeDisabledReasons).some(({ condition })=>condition() === true);
        },
        webIdeDisabledTooltip () {
            const disabledReason = Object.values(this.webIdeDisabledReasons).find((reason)=>reason.condition());
            return disabledReason?.message ?? '';
        },
        webIdeDisabledReasons () {
            return {
                queryErrors: {
                    condition: ()=>this.hasProjectQueryErrors,
                    message: this.$options.i18n.errorMessage
                },
                archived: {
                    condition: ()=>this.blobInfo.archived,
                    message: this.$options.i18n.archivedProjectTooltip
                },
                lfs: {
                    condition: ()=>this.isUsingLfs,
                    message: this.$options.i18n.lfsFileTooltip
                },
                fileModifyPermission: {
                    condition: ()=>!this.showWebIdeButton,
                    message: this.$options.i18n.noPermissionTooltip
                }
            };
        }
    },
    watch: {
        blobInfo () {
            initSourcegraph();
        }
    },
    methods: {
        formatTooltipWithShortcut (description, key) {
            return sanitize(`${description} <kbd class="flat gl-ml-1" aria-hidden=true>${key}</kbd>`);
        },
        handleFindFile () {
            this.trackEvent(FIND_FILE_BUTTON_CLICK);
            Shortcuts.focusSearchFile();
        },
        onCopy () {
            copyToClipboard(this.blobInfo.rawTextBlob);
        },
        onShowForkSuggestion () {
            this.isForkSuggestionModalVisible = true;
        },
        onEdit (target) {
            const { ideEditPath, editBlobPath } = this.blobInfo;
            const showForkSuggestionForSelectedEditor = forkSuggestionForSelectedEditor(target, this.shouldShowWebIdeForkSuggestion, this.shouldShowSingleFileEditorForkSuggestion);
            if (showForkSuggestionForSelectedEditor) {
                this.isForkSuggestionModalVisible = true;
            } else {
                visitUrl(isIdeTarget(target) ? ideEditPath : editBlobPath);
            }
        }
    }
};
